/**
 * Storage Service for browser localStorage operations
 * 
 * This service manages client-side caching using localStorage:
 * - Storing playback positions for each episode
 * - Managing cache lifecycle
 * - Generic get/set operations for any data type
 * 
 * Requirements: 4.5, 18.2
 */

import { STORAGE_KEYS, DEBUG } from '@/config';

/**
 * Storage Service interface defining all storage operations
 */
export interface StorageService {
  /**
   * Saves the playback position for an episode
   * 
   * @param episodeId - Unique episode identifier
   * @param position - Playback position in seconds
   * @param storyId - Story identifier (optional for backward compatibility)
   * 
   * Requirements: 4.5
   */
  savePlaybackPosition(episodeId: string, position: number, storyId?: string): void;

  /**
   * Retrieves the playback position for an episode
   * 
   * @param episodeId - Unique episode identifier
   * @param storyId - Story identifier (optional for backward compatibility)
   * @returns The saved playback position in seconds, or 0 if not found
   * 
   * Requirements: 4.5
   */
  getPlaybackPosition(episodeId: string, storyId?: string): number;

  /**
   * Clears all cached data from localStorage
   * 
   * This removes all playback positions and any other cached data.
   * 
   * Requirements: 18.2
   */
  clearCache(): void;

  /**
   * Generic get method for any data type
   * 
   * @param key - Storage key
   * @returns The cached data or null if not found
   */
  get<T>(key: string): T | null;

  /**
   * Generic set method for any data type
   * 
   * @param key - Storage key
   * @param value - Data to store
   */
  set<T>(key: string, value: T): void;
}

/**
 * Implementation of the StorageService using localStorage
 */
class LocalStorageService implements StorageService {
  /**
   * Saves the playback position for an episode
   * 
   * Positions are stored with a prefix to avoid key collisions.
   * Now includes storyId to maintain separate state per story.
   * 
   * @param episodeId - Unique episode identifier
   * @param position - Playback position in seconds
   * @param storyId - Story identifier (optional for backward compatibility)
   */
  savePlaybackPosition(episodeId: string, position: number, storyId?: string): void {
    try {
      const key = storyId 
        ? `${STORAGE_KEYS.PLAYBACK_PREFIX}${storyId}:${episodeId}`
        : `${STORAGE_KEYS.PLAYBACK_PREFIX}${episodeId}`;
      localStorage.setItem(key, position.toString());

      if (DEBUG) {
        console.log('[StorageService] Playback position saved', { storyId, episodeId, position });
      }
    } catch (error) {
      console.error('[StorageService] Failed to save playback position:', error);
      // Fail silently - playback will continue without position saving
    }
  }

  /**
   * Retrieves the playback position for an episode
   * 
   * @param episodeId - Unique episode identifier
   * @param storyId - Story identifier (optional for backward compatibility)
   * @returns The saved playback position in seconds, or 0 if not found
   */
  getPlaybackPosition(episodeId: string, storyId?: string): number {
    try {
      const key = storyId 
        ? `${STORAGE_KEYS.PLAYBACK_PREFIX}${storyId}:${episodeId}`
        : `${STORAGE_KEYS.PLAYBACK_PREFIX}${episodeId}`;
      const position = localStorage.getItem(key);

      if (!position) {
        return 0;
      }

      const parsed = parseFloat(position);

      // Validate that the position is a valid number
      if (isNaN(parsed) || parsed < 0) {
        console.warn('[StorageService] Invalid playback position in cache:', position);
        return 0;
      }

      if (DEBUG) {
        console.log('[StorageService] Playback position loaded', { storyId, episodeId, position: parsed });
      }

      return parsed;
    } catch (error) {
      console.error('[StorageService] Failed to retrieve playback position:', error);
      return 0;
    }
  }

  /**
   * Clears all cached data from localStorage
   * 
   * This removes all playback positions and any other app-specific data.
   */
  clearCache(): void {
    try {
      // Remove all playback positions
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(STORAGE_KEYS.PLAYBACK_PREFIX) || 
            key.startsWith(STORAGE_KEYS.STORY_MANIFEST_PREFIX)) {
          localStorage.removeItem(key);
        }
      });

      if (DEBUG) {
        console.log('[StorageService] Cache cleared');
      }
    } catch (error) {
      console.error('[StorageService] Failed to clear cache:', error);
    }
  }

  /**
   * Generic get method for any data type
   * 
   * @param key - Storage key
   * @returns The cached data or null if not found
   */
  get<T>(key: string): T | null {
    try {
      const serialized = localStorage.getItem(key);
      
      if (!serialized) {
        return null;
      }

      return JSON.parse(serialized) as T;
    } catch (error) {
      console.error(`[StorageService] Failed to retrieve ${key}:`, error);
      return null;
    }
  }

  /**
   * Generic set method for any data type
   * 
   * @param key - Storage key
   * @param value - Data to store
   */
  set<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);

      if (DEBUG) {
        console.log(`[StorageService] Saved ${key} to localStorage`);
      }
    } catch (error) {
      console.error(`[StorageService] Failed to save ${key}:`, error);
    }
  }
}

/**
 * Default storage service instance for use throughout the application
 */
export const storageService: StorageService = new LocalStorageService();
